import * as React from 'react';
import {
    CheckboxVisibility, CommandBarButton, ConstrainMode, DirectionalHint, ICommandBarItemProps, IconButton, IContextualMenuItem,
    IDialogContentProps, IObjectWithKey, MessageBar, MessageBarType, ScrollablePane, ScrollbarVisibility, Selection
} from 'office-ui-fabric-react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dictionary, EntityType, mapServerEntityType, updateCheckboxOptions } from '../../../entities/common';
import { ApplicationState } from '../../../store';
import { actionCreators as processActionCreators } from '../../../store/ProcessesListStore';
import { UserState } from "../../../store/User";
import { CommonOperations, contains } from "../../../store/permissions";
import Spinner from '../../common/Spinner';
import { bindActionCreators } from 'redux';
import * as Metadata from "../../../entities/Metadata";
import EntityDetailsList, { onRenderDetailsHeader } from '../../common/extensibleEntity/EntityDetailsList';
import RemoveDialog from '../../common/RemoveDialog';
import { IDeletionResult } from '../../../store/services/storeHelper';
import { canBeDeleted, IProcessAttrs, Process, ProcessStatus } from '../../../store/process/common';
import { groupBy, notUndefined } from '../../utils/common';
import { FilterHelper } from '../../../store/process/filters';
import { ActiveFilter as ProjectActiveFilter } from '../../../store/project/filters';
import ProcessCreation from '../../process/ProcessCreation';
import EntityGroupHeader, { EntityGroup } from '../../common/extensibleEntity/EntityGroupHeader';
import { nameof, namesof } from '../../../store/services/metadataService';
import SelectionModeSwitchableCommandBar from '../../common/SelectionModeSwitchableCommandBar';
import { ClearFilterComponent } from '../../common/sectionsControl/SectionPlaceholder';
import ListMenuFilterSelector from '../../common/ListMenuFilterSelector';
import { PPMFeatures, TenantState, Subscription } from '../../../store/Tenant';
import { buildSetProcessAsDefaultAction } from './ProcessHeader';

const buildProcessStatusFilters = (): Metadata.IFilter<Metadata.BaseFilterValue>[] => {
    return Object.entries(ProcessStatus)
        .filter(([_, value]) => typeof value === 'number')
        .map(([key, value]) => ({
            id: key,
            name: `${key} Processes`,
            attributeNames: [{ name: nameof<IProcessAttrs>('Status'), type: 'attributes' }],
            value: { attributes: { [nameof<IProcessAttrs>('Status')]: [value.toString()] } },
            isBuiltIn: true,
            isDefault: false,
            isPublic: true,
            isNotSaved: false,
        }));
}

const allProcessesFilter: Metadata.IFilter<Metadata.BaseFilterValue> = {
    id: "54a24bf5-4e3d-407a-9c07-dbae406bb6df",
    name: "All",
    attributeNames: [],
    value: {},
    isBuiltIn: true,
    isDefault: true,
    isPublic: true,
    isNotSaved: false,
};

const fakeFields: Metadata.Field[] = [
    {
        id: "129a2879-ea98-4b05-820e-ea8fb0bd2456",
        name: nameof<Process>("isDefault"),
        label: "Is Default",
        type: Metadata.FieldType.Flag,
        isFake: true,
        isNative: false,
        isReadonly: true,
        isCustom: false,
        isSystem: false,
        group: Metadata.FieldGroup.SystemFields,
        settings: {
            views: {
                list: {
                    componentPath: "process/IsDefault",
                    maxWidth: 150
                }
            }
        },
    }
];

const processFilters: Metadata.IFilter<Metadata.BaseFilterValue>[] = [
    allProcessesFilter,
    ...buildProcessStatusFilters(),
];

export const DefaultGroups: Metadata.Group[] = [
    {
        id: EntityType.Project,
        name: "Project Process",
        color: "#128a10",
        isShown: true,
        isDefault: true
    },
];

const processEntityType = EntityType.Process;

type StateProps = {
    fields: Metadata.Field[];
    user: UserState;
    tenant: TenantState;
    processes: Process[];
    isLoading: boolean;
    isListLoading: boolean;
    deletionResult?: IDeletionResult[];
};

type ActionProps = {
    processesActions: typeof processActionCreators;
};

export type Props = StateProps & ActionProps & RouteComponentProps<{}>;

const ProcessesList = (props: Props) => {
    const [selectedProcesses, setSelectedProcesses] = React.useState<Process[]>([]);
    const [newProcessEntityType, setNewProcessEntityType] = React.useState<EntityType>();
    const [processesToRemove, setProcessesToRemove] = React.useState<Process[]>([]);
    const [activeFilter, setActiveFilter] = React.useState(allProcessesFilter);
    
    const canManage = contains(props.user.permissions.common, CommonOperations.Administrate);
    const canCreateProcesses = Subscription.contains(props.tenant.subscription, PPMFeatures.ProcessCreation);

    React.useEffect(() => {
        props.processesActions.requestProcesses();
    }, []);

    const selection = React.useMemo(() => new Selection({
        getKey: (_: IObjectWithKey) => getProcessKey(_ as Process),
        onSelectionChanged: () => {
            setSelectedProcesses(selection.getSelection() as Process[]);
        }
    }), []);

    const entityFilterHelper = React.useMemo(() => new FilterHelper({ fields: props.fields }), [props.fields]);

    const applyFilter = (process: Process[]) => {
        return process.filter(isItemVisible);
    };

    const isItemVisible = (process: Process): boolean => {
        if (!activeFilter) {
            return true;
        }

        const filterValue = activeFilter.value;
        const allAttributes = entityFilterHelper.getFilterAttributes(props.fields);

        for (const type in filterValue) {
            if (!entityFilterHelper.helpersMap[type].validateItem(process, filterValue[type], allAttributes.filter(_ => _.type === type))) {
                return false;
            }
        }

        return true;
    };

    const updateProcessesStatus = (processes: Process[], status: ProcessStatus) => {
        const statusUpdates = processes.reduce((acc, process) => {
            acc[process.id] = { [nameof<IProcessAttrs>('Status')]: status };
            return acc;
        }, {} as Dictionary<Dictionary<ProcessStatus>>);
        props.processesActions.bulkUpdateProcesses(statusUpdates)
    };

    const buildActivateMenuItem = (processes: Process[]): IContextualMenuItem | undefined =>
        buildStatusItemIfStatusDiffersAndNotDefault(processes, ProcessStatus.Active, {
            key: "activate",
            name: "Activate",
            iconProps: { iconName: "CircleAddition" },
            onClick: () => updateProcessesStatus(processes, ProcessStatus.Active),
        });

    const buildDeactivateMenuItem = (processes: Process[]): IContextualMenuItem | undefined =>
        buildStatusItemIfStatusDiffersAndNotDefault(processes, ProcessStatus.Inactive, {
            key: "deactivate",
            name: "Deactivate",
            iconProps: { iconName: "Blocked2" },
            onClick: () => updateProcessesStatus(processes, ProcessStatus.Inactive),
        });

    const renderThreeDotsMenu = (process: Process) => {
        const items = [
            buildActivateMenuItem([process]),
            buildDeactivateMenuItem([process]),
            {
                key: 'view-related',
                name: `View Related Projects`,
                disabled: process.entitiesCount === 0,
                title: process.entitiesCount === 0 ? 'No Projects yet' : undefined,
                iconProps: { iconName: 'PPMXListView' },
                onClick: () => props.history.push(`/projects`, new ProjectActiveFilter("Custom").withProcess(process.id).buildLocationState())
            },
            buildSetProcessAsDefaultAction(process, props.processesActions.makeProcessDefault),
            {
                key: 'edit',
                name: process.isEditable ? 'Edit' : 'View',
                iconProps: { iconName: process.isEditable ? 'Edit' : 'View' },
                onClick: () => props.history.push(`/process/${process.id}`)
            },
            process.isEditable && canCreateProcesses ? {
                key: 'clone',
                name: 'Clone',
                iconProps: { iconName: 'Copy' },
                onClick: () => props.processesActions.cloneProcess(process.id)
            } : undefined,
            canBeDeleted(process) ? {
                key: 'delete',
                name: 'Delete',
                iconProps: { iconName: "Delete", style: { color: 'red' } },
                disabled: props.isLoading,
                style: {
                    color: (props.isLoading ? 'initial' : 'red'),
                    backgroundColor: (props.isLoading ? 'lightgrey' : 'unset')
                },
                onClick: () => setProcessesToRemove([process])
            } : undefined
        ].filter(notUndefined) as IContextualMenuItem[];

        return <div className="menu">
            <IconButton
                menuIconProps={{ iconName: 'PPMXMore' }}
                menuProps={{
                    directionalHint: DirectionalHint.bottomRightEdge,
                    items
                }}
            />
        </div>;
    }

    const buildStatusItemIfStatusDiffersAndNotDefault = (
        processes: Process[],
        status: ProcessStatus,
        updateStatusMenuItem: IContextualMenuItem
    ): IContextualMenuItem | undefined => {
        return processes.some(_ => _.attributes.Status !== status && !_.isDefault) ? updateStatusMenuItem : undefined;
    };
    
    const buildSelectionModeCommands = (): ICommandBarItemProps[] => {
        if (!canManage) {
            return [];
        }

        return [
            buildActivateMenuItem(selectedProcesses),
            buildDeactivateMenuItem(selectedProcesses),
            selectedProcesses.some(canBeDeleted)
                ? {
                    key: 'delete',
                    text: 'Delete',
                    iconProps: { iconName: 'Delete' },
                    onClick: () => setProcessesToRemove(selectedProcesses)
                } : undefined
        ].filter(notUndefined);
    }

    const buildListGroups = () => {
        const processesByEntityType = groupBy(props.processes, _ => mapServerEntityType[_.attributes.EntityType]!.toString());
        return DefaultGroups.map<EntityGroup>(_ => ({
            key: _.id,
            name: _.name,
            color: _.color,
            count: processesByEntityType[_.id]?.length ?? 0,
            hideMarker: true
        }));
    };

    const buildDeletionResultDialogContent = (deletionResult: IDeletionResult[]): IDialogContentProps => {
        if (deletionResult.length === 1) {
            return deletionResult[0].isDeleted
                ? {
                    title: "Process deletion is complete",
                    subText: `Process "${deletionResult[0].name}" was deleted successfully.`
                }
                : {
                    title: "Unable to delete process",
                    subText: "The selected process cannot be deleted."
                }
        }

        const deleted = deletionResult.filter(_ => _.isDeleted);
        return {
            title: "Processes deletion is completed",
            subText: `Selected processes (${deleted.length} items) were deleted successfully.`
        }
    }

    const buildCommands = (): IContextualMenuItem[] => {
        return [
            {
                key: 'new-process',
                onRender: () => (
                    <CommandBarButton
                        key="new-entity-header-button"
                        className='align-center new-entity-header-button'
                        iconProps={{ iconName: 'Add' }}
                        text="New"
                        menuProps={{
                            items: [
                                {
                                    key: 'emailMessage',
                                    text: 'New Project Process',
                                    iconProps: { iconName: 'Add' },
                                    onClick: () => setNewProcessEntityType(EntityType.Project)
                                },
                            ],
                            directionalHintFixed: true,
                        }}
                        title={!canCreateProcesses ? 'Upgrade your subscription to create new processes' : undefined}
                        disabled={!canManage || !canCreateProcesses}
                        styles={{ root: { height: 40, padding: '0 8px' } }}
                    />
                )
            }
        ];
    }

    const buildFarCommands = (): IContextualMenuItem[] => {
        return [
            {
                key: 'filter',
                onRender: () => (
                    <ListMenuFilterSelector
                        filters={processFilters}
                        activeFilter={activeFilter}
                        canManageConfiguration={canManage}
                        onActiveItemChanged={(id) => {
                            const newActiveFilter = processFilters.find(_ => _.id === id) ?? allProcessesFilter;
                            setActiveFilter(newActiveFilter);
                        }}
                    />
                )
            }
        ];
    }

    const isFieldFake = (field: Metadata.Field) => fakeFields.some(_ => _.id === field.id);

    const selectionModeCommands = buildSelectionModeCommands();

    if (props.isLoading || props.isListLoading) {
        return <Spinner />;
    }

    return <>
        <div className="processes">
            <h2>Process Management</h2>
            <div className="entities-list">
                <div className="entities-list-header">
                    <SelectionModeSwitchableCommandBar
                        items={buildCommands()}
                        farItems={buildFarCommands()}
                        selectionMode={{
                            enabled: selectedProcesses.length > 0,
                            items: selectionModeCommands,
                            selectedCount: selectedProcesses.length,
                            onCancel: () => selection.setAllSelected(false),
                        }}
                    />
                </div>
                <div key='list' className="entities-list-body list-container">
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <ClearFilterComponent
                            items={props.processes}
                            filteredItems={applyFilter(props.processes)}
                            onClearFilter={() => setActiveFilter(allProcessesFilter)}>
                            <EntityDetailsList
                                isFieldFake={isFieldFake}
                                getKey={getProcessKey}
                                entities={applyFilter(props.processes)}
                                entityType={processEntityType}
                                fields={props.fields.concat(fakeFields)}
                                displayFields={[nameof<IProcessAttrs>("Name"), nameof<Process>("isDefault"), ...namesof<IProcessAttrs>(["Status", "Description"])]}
                                selection={selection}
                                groups={buildListGroups()}
                                listGrouping={{
                                    getGroupKey: (process: Process) => mapServerEntityType[process.attributes.EntityType]!,
                                    renderGroupHeader: (group, isCollapsed, isSelected, onToggleCollapse, onToggleSelect) => (
                                        <EntityGroupHeader
                                            group={group}
                                            isCollapsed={isCollapsed}
                                            isSelected={isSelected}
                                            onToggleCollapse={onToggleCollapse}
                                            onToggleSelect={onToggleSelect}
                                            renderCounter={(grp) => <span className="subitems-counter">{grp.count}</span>}
                                        />
                                    ),
                                }}
                                onItemMenuRender={renderThreeDotsMenu}
                                {...updateCheckboxOptions({
                                    checkboxVisibility: !canManage ? CheckboxVisibility.hidden : undefined
                                }, selectionModeCommands, true)}
                                fabricListProps={{
                                    constrainMode: ConstrainMode.unconstrained,
                                    onRenderDetailsHeader: onRenderDetailsHeader,
                                }}
                            />
                        </ClearFilterComponent>
                    </ScrollablePane>
                </div>
            </div>
        </div>
        {newProcessEntityType !== undefined && (
            <ProcessCreation
                processEntityType={newProcessEntityType}
                onDismiss={() => setNewProcessEntityType(undefined)}
            />
        )}
        {!!processesToRemove.length && (
            <RemoveDialog
                onClose={() => setProcessesToRemove([])}
                onComplete={() => props.processesActions.removeProcesses(processesToRemove.map(_ => _.id))}
                dialogContentProps={buildRemoveDialogContentProps(processesToRemove)}
                confirmButtonProps={{ text: "Delete", disabled: processesToRemove.length === 0 }}
            >
            </RemoveDialog>
        )}
        {props.deletionResult && (
            <RemoveDialog
                onClose={() => props.processesActions.dismissDeletionResult()}
                confirmButtonProps={{ text: "Got it" }}
                modalProps={{ styles: { main: { minWidth: 500 } } }}
                dialogContentProps={buildDeletionResultDialogContent(props.deletionResult)}
            >
                {props.deletionResult.length > 1 && props.deletionResult.some(_ => !_.isDeleted) && (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
                        Failed to delete the following processes:
                        <ul>
                            {props.deletionResult.filter(_ => !_.isDeleted).map(_ => <li key={_.id}>{_.message}</li>)}
                        </ul>
                    </MessageBar>
                )}
            </RemoveDialog>
        )}
    </>;
};

const getProcessKey = (process: Process): string => `${process.id}_${process.attributes.Status}`;

const buildRemoveDialogContentProps = (processesToRemove: Process[]): IDialogContentProps => {
    if (processesToRemove.length === 1) {
        return {
            title: "Delete process",
            subText: `Are you sure you want to delete process "${processesToRemove[0].attributes.Name}"?`
        }
    }

    return {
        title: "Delete processes",
        subText: `Are you sure you want to delete selected processes (${processesToRemove.length} items)?`
    }
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const fields = state.fields[processEntityType];
    return {
        fields: fields.allIds.map(_ => fields.byId[_]),
        user: state.user,
        tenant: state.tenant,
        processes: state.processes.allIds.map(_ => state.processes.byId[_]),
        isLoading: state.processes.isLoading,
        isListLoading: state.processes.isListLoading,
        deletionResult: state.processes.deletionResult,
    };
}

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        processesActions: bindActionCreators(processActionCreators, dispatch),
    };
}

export default connect(mapStateToProps, mergeActionCreators)(ProcessesList);
